/**
 * Image Carousel Component
 * Displays a carousel of images with navigation controls
 * Used for style/template preview images
 */

import { useState, useMemo } from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';

interface ImageCarouselProps {
  previewImage?: string;
  images?: string[];
  styleName: string;
  styleId: string;
  className?: string;
}

export function ImageCarousel({ previewImage, images = [], styleName, styleId, className = '' }: ImageCarouselProps) {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [failedIndices, setFailedIndices] = useState<Set<number>>(new Set());

  // Helper function to resolve image URLs
  const resolveImageUrl = (imagePath: string, styleId: string) => {
    // If it's already a full URL, return as is
    if (imagePath.startsWith('http') || imagePath.startsWith('/')) {
      return imagePath;
    }
    // Otherwise, construct path relative to public/styles/{styleId}/
    return `/styles/${styleId}/${imagePath}`;
  };

  // Combine preview image and additional images with resolved URLs
  // Use Set to deduplicate in case previewImage is also in images array
  const allImages = useMemo(() => {
    const urls = [
      ...(previewImage ? [resolveImageUrl(previewImage, styleId)] : []),
      ...images.map(img => resolveImageUrl(img, styleId))
    ];
    // Deduplicate URLs to prevent showing same image multiple times
    return Array.from(new Set(urls));
  }, [previewImage, images, styleId]);

  if (allImages.length === 0) {
    return (
      <div className={`w-full h-full bg-gradient-to-br from-gray-100 to-gray-200 dark:from-gray-700 dark:to-gray-800 rounded-lg border flex items-center justify-center ${className}`}>
        <span className="text-sm text-gray-500 dark:text-gray-400">No Preview</span>
      </div>
    );
  }

  const handleImageError = () => {
    setFailedIndices(prev => {
      const next = new Set(prev);
      next.add(currentIndex);
      return next;
    });
  };

  const nextImage = () => {
    setCurrentIndex((prev) => (prev + 1) % allImages.length);
  };

  const prevImage = () => {
    setCurrentIndex((prev) => (prev - 1 + allImages.length) % allImages.length);
  };

  const currentImageFailed = failedIndices.has(currentIndex);

  return (
    <div className={`relative w-full h-full group ${className}`}>
      {/* Conditionally render image or placeholder based on error state */}
      {!currentImageFailed ? (
        <img
          src={allImages[currentIndex]}
          alt={`${styleName} preview ${currentIndex + 1}`}
          className="w-full h-full object-cover rounded-lg border shadow-sm"
          onError={handleImageError}
        />
      ) : (
        <div className="w-full h-full bg-gradient-to-br from-gray-100 to-gray-200 dark:from-gray-700 dark:to-gray-800 rounded-lg border flex items-center justify-center">
          <span className="text-sm text-gray-500 dark:text-gray-400">Preview</span>
        </div>
      )}

      {allImages.length > 1 && (
        <>
          {/* Navigation buttons */}
          <button
            onClick={(e) => {
              e.stopPropagation();
              prevImage();
            }}
            className="absolute left-1 top-1/2 -translate-y-1/2 w-6 h-6 bg-black/60 hover:bg-black/80 text-white rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-all duration-200"
            title="Previous image"
          >
            <ChevronLeft className="h-3 w-3" />
          </button>
          <button
            onClick={(e) => {
              e.stopPropagation();
              nextImage();
            }}
            className="absolute right-1 top-1/2 -translate-y-1/2 w-6 h-6 bg-black/60 hover:bg-black/80 text-white rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-all duration-200"
            title="Next image"
          >
            <ChevronRight className="h-3 w-3" />
          </button>

          {/* Image indicators */}
          <div className="absolute bottom-1 left-1/2 -translate-x-1/2 flex gap-1 px-2 py-1 bg-black/50 rounded-full opacity-0 group-hover:opacity-100 transition-opacity duration-200">
            {allImages.map((_, index) => (
              <div
                key={index}
                className={`w-1.5 h-1.5 rounded-full transition-colors ${
                  index === currentIndex ? 'bg-white' : 'bg-white/40'
                }`}
              />
            ))}
          </div>
        </>
      )}
    </div>
  );
}
